import {BaseFacade} from '../../../core/abstracts/base-facade.abstract';
import {Injectable} from '@angular/core';
import {ReportService} from '../service/report.service';
import {BehaviorSubject, catchError, finalize, forkJoin, map, Observable, of, Subject, tap} from 'rxjs';
import {AvailableReportColumn} from '../model/available-report-column.model';
import {AvailableReportColumnResponseMapper} from '../mapper/available-report-column-response.mapper';
import {ReportRequest} from '../model/report-request.model';
import {MessageService} from 'primeng/api';
import {VehicleCategoryService} from '../../entities/category/services/vehicle-category.service';
import {VehicleCategoryResponseMapper} from '../../entities/category/mappers/vehicle-category-response.mapper';
import {VehicleCategory} from '../../entities/category/models/vehicle-category.model';
import {Brand} from '../../entities/brand/models/brand.model';
import {VehicleBrandService} from '../../entities/brand/services/vehicle-brand.service';
import {VehicleBrandResponseMapper} from '../../entities/brand/mapper/vehicle-brand-response.mapper';
import {VehicleModel} from '../../entities/model/model/vehicle-model.model';
import {VehicleModelService} from '../../entities/model/service/vehicle-model.service';
import {VehicleModelsResponseMapper} from '../../entities/model/mapper/vehicle-models-response.mapper';
import {ReportResponse} from '../model/report-response.model';
import {saveAs} from 'file-saver';
import {ReportFormValue} from '../model/report-form.model';
import {ReportVehicleDetailsMapper} from '../mapper/report-vehicle-details.mapper';
import {ReportRequestMapper} from '../mapper/report-request.mapper';
import {SelectLoadMoreEvent} from '../../../shared/components/input/select-input/select-input.component';
import {PageableRequest} from '../../../core/models/pagable.model';
import {StorageSearchFilterRequest} from '../../entities/storage/model/storage-search-filter-request.model';
import {VehicleStorageService} from '../../entities/storage/service/vehicle-storage.service';
import {StoragePageableResponseMapper} from '../../entities/storage/mapper/storage-pageable-response.mapper';
import {Page} from '../../../core/models/page-response.model';
import {VehicleStorage} from '../../entities/storage/model/storage.model';
import {Owner} from '../../entities/owners/models/owner.model';
import {VehicleOwnerService} from '../../entities/owners/services/vehicle-owner-response.service';
import {
  VehicleOwnersPageableResponseMapper
} from '../../entities/owners/mappers/vehicle-owners-pageable-response.mapper';
import {OwnerSearchFilterRequest} from '../../entities/owners/models/owner-search-filter-request.model';
import {ReportFetchStatus} from '../component/report-component/report.component';

@Injectable({providedIn: 'root'})
export class ReportFacade extends BaseFacade {

  private readonly columnSubject: BehaviorSubject<AvailableReportColumn[]> = new BehaviorSubject<AvailableReportColumn[]>([]);
  readonly availableReportColumns$ = this.columnSubject.asObservable();

  private readonly vehicleCategoriesSubject: BehaviorSubject<VehicleCategory[]> = new BehaviorSubject<VehicleCategory[]>([]);
  readonly vehicleCategories$ = this.vehicleCategoriesSubject.asObservable();

  private readonly vehicleBrandsSubject: BehaviorSubject<Brand[]> = new BehaviorSubject<Brand[]>([]);
  readonly vehicleBrands$ = this.vehicleBrandsSubject.asObservable();

  private readonly vehicleModelsSubject: BehaviorSubject<VehicleModel[]> = new BehaviorSubject<VehicleModel[]>([]);
  readonly vehicleModels$ = this.vehicleModelsSubject.asObservable();

  private readonly vehicleStoragesSubject: BehaviorSubject<Page<VehicleStorage> | null> = new BehaviorSubject<Page<VehicleStorage> | null>(null);
  readonly vehicleStorages$ = this.vehicleStoragesSubject.asObservable();

  private readonly vehicleOwnersSubject: BehaviorSubject<Page<Owner> | null> = new BehaviorSubject<Page<Owner> | null>(null);
  readonly vehicleOwners$ = this.vehicleOwnersSubject.asObservable();

  private readonly errorSubject = new Subject<Error | null>();
  readonly error$ = this.errorSubject.asObservable();

  constructor(
    private readonly reportService: ReportService,
    private readonly messageService: MessageService,
    private readonly availableReportColumnResponseMapper: AvailableReportColumnResponseMapper,
    private readonly vehicleCategoryService: VehicleCategoryService,
    private readonly vehicleCategoryResponseMapper: VehicleCategoryResponseMapper,
    private readonly vehicleBrandService: VehicleBrandService,
    private readonly vehicleBrandResponseMapper: VehicleBrandResponseMapper,
    private readonly vehicleModelService: VehicleModelService,
    private readonly vehicleModelResponseMapper: VehicleModelsResponseMapper,
    private readonly reportVehicleDetailsMapper: ReportVehicleDetailsMapper,
    private readonly reportRequestMapper: ReportRequestMapper,
    private readonly vehicleStorageService: VehicleStorageService,
    private readonly storagePageableResponseMapper: StoragePageableResponseMapper,
    private readonly vehicleOwnersService: VehicleOwnerService,
    private readonly vehicleOwnersPageableResponseMapper: VehicleOwnersPageableResponseMapper
  ) {
    super();
  }

  public fetchAvailableReportColumns(): void {
    this.startLoading();

    this.reportService.getAll().subscribe({
      next: (availableReportColumnResponse) => {
        const columns = this.availableReportColumnResponseMapper.mapArray(availableReportColumnResponse);
        this.columnSubject.next(columns);
        this.stopLoading();
      },
      error: (error) => {
        this.errorSubject.next(error);
        this.stopLoading();
      }
    })
  }

  public fetchVehicleCategories(): void {
    this.vehicleCategoryService.getAll().subscribe({
      next: (vehicleCategoriesResponse) => {
        const vehicleCategories = this.vehicleCategoryResponseMapper.mapArray(vehicleCategoriesResponse);
        this.vehicleCategoriesSubject.next(vehicleCategories);
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  public fetchVehicleBrands(): void {
    this.vehicleBrandService.getAll().subscribe({
      next: (vehicleBrandsResponse) => {
        const vehicleBrands = this.vehicleBrandResponseMapper.mapArray(vehicleBrandsResponse);
        this.vehicleBrandsSubject.next(vehicleBrands);
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  public fetchVehicleModelsByBrands(brandIds: number[]): void {
    if (brandIds.length === 0) {
      this.vehicleModelsSubject.next([]);
      return;
    }

    forkJoin(
      brandIds.map(id =>
        this.vehicleModelService.getById(id).pipe(
          map(response => this.vehicleModelResponseMapper.mapArray(response)),
          catchError(() => of([]))
        )
      )
    ).subscribe({
      next: (modelsArrays) => {
        const allModels = modelsArrays.flat();
        this.vehicleModelsSubject.next(allModels);
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  public fetchReport(data: ReportFormValue): Observable<ReportFetchStatus> {
    this.startLoading();

    const reportScheme = data.reportSchemeSection.selectedReportColumns;
    const itemsDetails = this.reportVehicleDetailsMapper.map(data);
    const payload: ReportRequest = this.reportRequestMapper.map({reportScheme, itemsDetails});

    return this.reportService.create(payload).pipe(
      tap((reportResponse: ReportResponse) => {
        this.encodeAndDownload(reportResponse);
      }),
      map(() => ReportFetchStatus.SUCCESS),
      tap(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Generowanie raportu',
          detail: 'Raport stanu magazynowego jest gotowy!'
        });
      }),
      catchError((error) => {
        this.errorSubject.next(error);
        this.messageService.add({severity: 'error', summary: 'Error', detail: 'Report generation failed!'});
        return of(ReportFetchStatus.ERROR);
      }),
      finalize(() => this.stopLoading())
    );
  }

  public fetchVehicleStorages(lazyLoadEvent: SelectLoadMoreEvent): void {
    let pagination: PageableRequest = new PageableRequest(lazyLoadEvent.page, lazyLoadEvent.size);
    let filters: StorageSearchFilterRequest = {name: lazyLoadEvent.query};

    this.vehicleStorageService.getPage(pagination, filters).subscribe({
      next: (vehicleStoragesResponse) => {
        const vehicleStorages = this.storagePageableResponseMapper.map(vehicleStoragesResponse);
        this.vehicleStoragesSubject.next(vehicleStorages);
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  public fetchVehicleOwners(lazyLoadEvent: SelectLoadMoreEvent): void {
    let pagination: PageableRequest = new PageableRequest(lazyLoadEvent.page, lazyLoadEvent.size);
    let filters: OwnerSearchFilterRequest = {name: lazyLoadEvent.query};

    this.vehicleOwnersService.getPage(pagination, filters).subscribe({
      next: (vehicleOwnersResponse) => {
        const vehicleOwners = this.vehicleOwnersPageableResponseMapper.map(vehicleOwnersResponse);
        this.vehicleOwnersSubject.next(vehicleOwners);
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  private encodeAndDownload(response: ReportResponse): void {
    const binaryString = atob(response.contentBase64);
    const bytes = Uint8Array.from(binaryString, char => char.charCodeAt(0) & 0xff);
    const blob = new Blob([bytes.buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    saveAs(blob, response.filename);
  }
}
